import { Component, Input, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
import { CommonToastService } from '@common2/services/toast.service';
import { Company, Journey, JourneyProvider } from '@lighty';
import { StorageService, TranslateService } from '@services';
import { finalize } from 'rxjs';
import { environment as env } from '@env/environment';
import { UrlHelper } from '@helpers';

const DEFAULT_PROVIDER_LIST_KEYS: ReadonlyArray<string> = [
    'appcues',
    'bookboon',
    'cefora',
    'cegos',
    'coursera',
    'edflex-blue',
    'email-apside',
    'freshchat',
    'goodhabitz',
    'gotowebinar',
    'learncube',
    'mymooc',
    'openclassroom',
    'trainingexpress',
    'udemy',
    'youtube',
    'linkedin',
    'aws',
    'learninghubz',
    'coursera-for-campus',
    'boclips',
    'netzun',
    'onementor',
    'coursera-career-academy',
    'xertify',
    'power_bi_dashboards',
    'platzi',
    'coursera-multi-programs'
];

@Component({
    selector: 'msc-card-journey',
    templateUrl: './card-journey.component.html',
    styleUrls: ['./card-journey.component.scss']
})

export class CardJourneyComponent {
    @Input() journey: Journey;
    @Input() saved: boolean;
    @Input() params?: any;
    @Input() deleteEmitter: EventEmitter<boolean> = new EventEmitter();
    @Output() onEvent: EventEmitter<any> = new EventEmitter();
    public company: Company;

    constructor(
        private router: Router,
        private journeyProvider: JourneyProvider,
        private storageService: StorageService,
        private toastService: CommonToastService,
        private translateService: TranslateService,
    ) { }

    get logo(): string { return this.getLogo(this.journey); }

    ngOnInit(): void {
        this.company = this.storageService.get('company');

        this.deleteEmitter.subscribe(() => {
            this.journeyProvider.unsubscribe(this.journey.id).pipe(finalize(() => {
                this.onEvent.emit('completeDelete');
            })).subscribe(() => {
                this.toastService.onSuccess(this.translateService.instant('toast.deleted'));
            });
        });
    }

    unsubscribeJourney(): void {
        this.onEvent.emit('delete');
    }

    redirectToJourney(): void {
        this.storageService.set('card_redirect', 'summary');
        const url = `learn/${this.company.slug}/detail/journey/${this.journey.id}`;
        this.router.navigateByUrl(url, { state: { progress: (this.journey as any).progress ?? (this.journey as any).session?.progress ?? 0 } });
    }

    goToPlayer(): void {
        this.journeyProvider
            .resumeJourney(this.company.id, this.journey.id)
            .subscribe(({ scope, url }) => {
                const localUrl = this.getLocalUrl(scope)
                UrlHelper.navigate(localUrl + '/' + url, '_self');
            });
    }

    getLocalUrl(scope: string) {
        const localUrl = UrlHelper.getProtocol() + '//' + UrlHelper.getHost();
        switch (true) {
            case scope === 'app': return `${localUrl}/learn`;
            case scope === 'player': return localUrl === env.envVar.APP_URL ? env.envVar.PLAYER_URL : `${localUrl}/player`;
            default: return localUrl;
        }
    }

    changeLearnLaterContent(): void {
        if (this.saved) {
            this.deleteSavedContent();
        } else {
            this.addToSavedContents();
        }
    }

    addToSavedContents(): void {
        this.saved = true;
        this.onEvent.emit('addSavedContent');
    }

    deleteSavedContent(): void {
        this.saved = false;
        this.onEvent.emit('deleteSavedContent');
    }

    stopPropagation(event: any): void {
        event.stopPropagation();
    }

    getLogo(datum: any) {
        switch (true) {
            // If there is a customCampMedia , you need to display this media
            case !!datum && datum.hasOwnProperty('customCampMedia') && !!datum.customCampMedia:
                return `url(${datum?.customCampMedia?.pictureUrl}?size=256)`;
            // If there is NO customCampMedia , you need to check whether there is a provider.media
            case !!datum && datum.hasOwnProperty('provider') && !!datum.provider && datum.provider.hasOwnProperty('media') && !!datum?.provider?.media:
                // If company.id === provider.responsibleId && provider.responsibleType === 'company')  => display the company_logo and not the provider.media logo
                return this.company?.id === datum.provider.responsibleId && datum.provider.responsibleType === 'company' ?
                    `url(${this.company?.experience?.displayPicture}?size=256)` : `url(${datum.provider.media.pictureUrl}?size=256)`;
            // If there is no  provider.media  check if provider is Coursera, Goodhabitz, Bookboon etc...
            case !!datum && datum.hasOwnProperty('provider') && DEFAULT_PROVIDER_LIST_KEYS.includes(datum?.provider?.key):
                return `url(https://static.myskillcamp.com/images/integrations/${datum.provider.key}.svg)`;
            // If there is no  provider.media + NOT IN know providers , you need to display the company media image
            default:
                return `url(${this.company?.experience?.displayPicture}?size=256)`;
        }
    }
}
